import React, { Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Main } from './styled';
import Spinner from '../components/utilities/Spinner';
import { DataService } from '../config/data/dataService';
import {
  Card, Table, Button, Row, Col, Typography,
  Space, Tag, Input, Select, Avatar, message
} from 'antd';
import {
  EyeOutlined, CalendarOutlined, SearchOutlined, FilterOutlined,
  MobileOutlined, HistoryOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const PAGE_SIZE = 10;

const parseDeviceImages = (deviceImage) => {
  if (!deviceImage) return [];
  if (Array.isArray(deviceImage)) return deviceImage;

  const value = String(deviceImage).trim();

  try {
    const parsedImages = JSON.parse(value);
    if (Array.isArray(parsedImages)) return parsedImages;
  } catch (error) {
    // History image payload can arrive as [url,url] instead of valid JSON.
  }

  const markdownLinkMatch = value.match(/\((https?:\/\/.*)\)$/);
  const imageList = markdownLinkMatch ? markdownLinkMatch[1] : value.replace(/^\[/, '').replace(/\]$/, '');

  return imageList
    .split(',')
    .map((url) => url.trim())
    .filter((url) => url.startsWith('http'));
};

const parseAnswers = (answers, labels = {}) => {
  if (!answers) return [];

  try {
    const parsedAnswers = typeof answers === 'string' ? JSON.parse(answers) : answers;
    return Object.keys(parsedAnswers || {})
      .sort((a, b) => Number(a) - Number(b))
      .map((id) => ({
        id: Number(id),
        question: labels[id] || `Question ${id}`,
        answer: parsedAnswers[id],
      }));
  } catch (error) {
    console.warn('Unable to parse history answers:', error);
    return [];
  }
};

const getHighestBidAmount = (data) => {
  const amount = Number(data.highest_bid_amount ?? 0);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
};

const formatBidStatus = (status) => {
  if (!status) return 'Pending';
  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const mapHistoryRow = (row, labels) => ({
  id: row.reference_no || row.exchange_reference_id,
  referenceNo: row.reference_no || row.exchange_reference_id,
  storeCode: row.store_code,
  storeName: row.store_name,
  storeAddress: row.address,
  storeEmail: row.email,
  storeMobile: row.mobile,
  customerName: row.customer_name || '-',
  customerMobile: row.customer_mobile,
  imeiNumber: row.imei_number,
  deviceModel: [row.brand, row.model].filter(Boolean).join(' ') || row.model || '-',
  bidAmount: row.bid_amount,
  highestBid: getHighestBidAmount(row),
  bidStatus: row.bid_status,
  status: formatBidStatus(row.bid_status),
  rawStatus: row.status,
  date: row.evb_created_at || row.er_created_at || row.created_at || '-',
  createdAt: row.er_created_at,
  updatedAt: row.er_updated_at,
  bidCreatedAt: row.evb_created_at,
  bidUpdatedAt: row.evb_updated_at,
  vendorCode: row.vendor_code_id,
  exchangeReferenceId: row.exchange_reference_id,
  quotedAmount: Number(row.bid_amount) || 0,
  initialQuote: Number(row.bid_amount) || 0,
  condition: String(row.device_condition ?? '').trim() || '-',
  answersRaw: row.answers,
  images: parseDeviceImages(row.device_image),
  checkDetails: parseAnswers(row.answers, labels),
});

const History = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [historyRows, setHistoryRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [vendorQuestions, setVendorQuestions] = useState({});

  const fetchHistory = useCallback(async (page = 1, search = searchText) => {
    setLoading(true);
    try {
      const response = await DataService.post('/getHistoryList', {
        page: String(page),
        page_items_count: PAGE_SIZE,
        search: search.trim(),
      });
      const responseData = response?.data?.data;
      const historyRows = responseData?.data || [];
      console.log('Exchange submit history response:', response?.data);
      setHistoryRows(historyRows);
      setTotalCount(Number(responseData?.total_count) || 0);
      setCurrentPage(Number(responseData?.current_page) || page);
    } catch (error) {
      console.error('Exchange submit history error:', error);
      message.error('Unable to fetch quote history');
      setHistoryRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [searchText]);

  useEffect(() => {
    const fetchVendorQuestions = async () => {
      try {
        const response = await DataService.get('/getVendorQuestions');
        if (response.data.status !== true) {
          throw new Error(response.data.message || 'Unable to fetch vendor questions');
        }
        setVendorQuestions(Object.fromEntries(
          response.data.data.map(({ id, question }) => [id, question]),
        ));
      } catch (error) {
        console.error('History vendor questions error:', error);
      }
    };

    fetchVendorQuestions();
  }, []);

  useEffect(() => {
    const searchTimer = setTimeout(() => {
      fetchHistory();
    }, 350);

    return () => clearTimeout(searchTimer);
  }, [fetchHistory]);

  const getStatusColor = (status) => {
    const normalizedStatus = String(status || '').toLowerCase();
    if (normalizedStatus.includes('accepted')) return 'success';
    if (normalizedStatus.includes('rejected')) return 'error';
    if (normalizedStatus.includes('pending') || normalizedStatus.includes('submitted') || normalizedStatus.includes('quoted')) return 'warning';
    return 'processing';
  };

  const handleViewDetails = (record) => {
    navigate('/History/details', { state: { referenceNo: record.referenceNo || record.id } });
  };

  const applications = useMemo(
    () => historyRows.map((row) => mapHistoryRow(row, vendorQuestions)),
    [historyRows, vendorQuestions],
  );
  const filteredHistory = applications.filter(app => statusFilter === 'All' || app.status === statusFilter);

  const columns = [
    {
      title: 'App ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => <Text strong style={{ color: '#1890ff' }}>{text}</Text>
    },
    {
      title: 'Device Model',
      dataIndex: 'deviceModel',
      key: 'deviceModel',
      render: (text, record) => (
        <Space size="middle">
          <Avatar src={record.images[0]} icon={<MobileOutlined />} shape="square" style={{ borderRadius: '6px' }} />
          <Text strong>{text}</Text>
        </Space>
      )
    },
    {
      title: 'Quoted Price',
      dataIndex: 'bidAmount',
      key: 'bidAmount',
      render: (amount) => <Text strong>{amount ? `₹${Number(amount).toLocaleString('en-IN')}` : 'N/A'}</Text>
    },
    {
      title: 'Highest Bid',
      dataIndex: 'highestBid',
      key: 'highestBid',
      render: (amount) => (
        amount > 0 ? (
          <div style={{
            background: '#fff7e6',
            border: '1px solid #ffd591',
            borderRadius: '6px',
            display: 'inline-block',
            padding: '4px 10px',
          }}>
            <Text strong style={{ color: '#d46b08' }}>
              ₹{Number(amount).toLocaleString('en-IN')}
            </Text>
          </div>
        ) : '-'
      )
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text>{name}</Text>
          {record.customerMobile && <Text type="secondary" style={{ fontSize: '12px' }}>{record.customerMobile}</Text>}
        </Space>
      )
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => (
        <Space size="small">
          <CalendarOutlined style={{ color: '#bfbfbf' }} />
          <Text>{date}</Text>
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)} style={{ minWidth: '85px', textAlign: 'center' }}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          type="primary"
          ghost
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record)}
          size="small"
        >
          View details
        </Button>
      )
    }
  ];

  return (
    <Main>
      <Suspense fallback={<Spinner />}>
        <div>
          <Row justify="space-between" align="middle" style={{ marginBottom: '32px' }} gutter={[16, 16]}>
            <Col xs={24} md={10}>
              <Title level={3} style={{ margin: 0 }}>
                <HistoryOutlined style={{ marginRight: '12px', color: '#1890ff' }} />
                Quote History
              </Title>
            </Col>
            <Col xs={24} md={14} style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap', width: '100%' }}>
              <Input
                placeholder="Search ID or Model..."
                prefix={<SearchOutlined style={{ color: '#bfbfbf', marginRight: '4px' }} />}
                size="large"
                allowClear
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ maxWidth: '300px', width: '100%', borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}
              />
              <Select
                defaultValue="All"
                size="large"
                onChange={(val) => setStatusFilter(val)}
                style={{ width: '160px', borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.02)' }}
                suffixIcon={<FilterOutlined />}
              >
                <Option value="All">All Statuses</Option>
                <Option value="Accepted">Accepted</Option>
                <Option value="Rejected">Rejected</Option>
                <Option value="Pending">Pending</Option>
                <Option value="Received">Received</Option>
              </Select>
            </Col>
          </Row>

          <Card
            variant="borderless"
            style={{ boxShadow: '0 4px 12px rgba(0,0,0,0.02)', borderRadius: '12px', overflow: 'hidden' }}
            styles={{ body: { padding: 0 } }}
          >
            <Table
              columns={columns}
              dataSource={filteredHistory}
              loading={loading}
              pagination={totalCount > PAGE_SIZE ? {
                current: currentPage,
                pageSize: PAGE_SIZE,
                total: totalCount,
                showSizeChanger: false,
                onChange: (page) => fetchHistory(page),
              } : false}
              style={{ borderRadius: '12px' }}
              scroll={{ x: 'max-content' }}
              rowKey="id"
            />
          </Card>
        </div>
      </Suspense>

      <style>{`
        .device-preview-img {
          transition: transform 0.3s ease;
        }
        .device-preview-img:hover {
          transform: scale(1.05);
        }
      `}</style>
    </Main>
  );
}

export default History;
